import React, { useState, useEffect } from 'react';
import { AppIcon } from '../AppIcon';
import { iconService } from '../../services/iconService';
import { BaseEntityType } from '../../types/entities';
import { AppIcon as AppIconType } from '../../types/apps';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('ICON_COMPONENT');

interface IconProps {
  // Entity-based icon (highest priority)
  entity?: BaseEntityType;
  
  // App-based icon
  appId?: string;
  
  // File-based icon (by filename/extension)
  filename?: string;
  
  // Direct icon config (bypass service)
  icon?: string | AppIconType;
  
  // Standard AppIcon props
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  className?: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  
  // Context for icon resolution (future use)
  context?: string;
}

/**
 * Universal Icon component that handles all icon types through centralized service
 * 
 * Usage examples:
 * <Icon entity={pdfEntity} size="medium" />
 * <Icon appId="pdfium" size="large" />
 * <Icon filename="document.pdf" size="small" />
 * <Icon icon={{ type: 'emoji', emoji: '📄' }} size="medium" />
 */
const IconComponent: React.FC<IconProps> = ({
  entity,
  appId,
  filename,
  icon,
  size = 'medium',
  className = '',
  width,
  height,
  style,
  context
}) => {
  const [iconConfig, setIconConfig] = useState<AppIconType>({ type: 'emoji', emoji: '⬜️' });

  useEffect(() => {
    const resolveIcon = async () => {
      try {
        // Direct icon prop has highest priority
        if (icon) {
          if (typeof icon === 'string') {
            // String could be emoji or phosphor icon name
            // If it looks like phosphor (capitalized), treat as phosphor
            if (icon[0] === icon[0].toUpperCase() && icon.length > 1) {
              setIconConfig({ 
                type: 'phosphor' as const, 
                phosphorIcon: icon,
                phosphorWeight: 'duotone',
                metadata: { isAppIcon: true }
              });
            } else {
              setIconConfig({ type: 'emoji' as const, emoji: icon });
            }
            return;
          }
          // ✅ Add metadata if missing (required for proper rendering)
          const iconWithMetadata = {
            ...icon,
            metadata: {
              ...icon.metadata,
              isAppIcon: icon.metadata?.isAppIcon ?? true
            }
          };
          setIconConfig(iconWithMetadata);
          return;
        }
        
        // Entity-based icon resolution (now async)
        if (entity) {
          const result = await iconService.getEntityIcon(entity, context);
          setIconConfig(result);
          return;
        }
        
        // App-based icon resolution
        if (appId) {
          const result = iconService.getAppIcon(appId);
          setIconConfig(result);
          return;
        }
        
        // File-based icon resolution
        if (filename) {
          const fileIcon = iconService.getFileIcon(filename);
          if (fileIcon) {
            setIconConfig(fileIcon);
            return;
          }
        }
        
        // Ultimate fallback
        setIconConfig({ type: 'emoji' as const, emoji: '📄' });
      } catch (error) {
        log.error('Error in Icon component:', error);
        setIconConfig({ type: 'emoji' as const, emoji: '⚠️' });
      }
    };

    resolveIcon();
  }, [entity, appId, filename, icon, context]);

  return (
    <AppIcon 
      icon={iconConfig} 
      size={size} 
      className={className}
      width={width}
      height={height}
      style={style}
    />
  );
};

// Memoize the component with smart comparison to prevent unnecessary re-renders
export const Icon = React.memo(IconComponent, (prevProps, nextProps) => {
  // Compare direct props first (most common case)
  if (prevProps.appId !== nextProps.appId ||
      prevProps.filename !== nextProps.filename ||
      prevProps.size !== nextProps.size ||
      prevProps.className !== nextProps.className ||
      prevProps.width !== nextProps.width ||
      prevProps.height !== nextProps.height ||
      prevProps.context !== nextProps.context) {
    return false;
  }

  // Compare style prop (shallow comparison)
  if (prevProps.style !== nextProps.style) {
    if (typeof prevProps.style === 'object' && typeof nextProps.style === 'object') {
      return JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style);
    }
    return false;
  }

  // Compare icon prop (could be string or object)
  if (prevProps.icon !== nextProps.icon) {
    // For objects, do deep comparison
    if (typeof prevProps.icon === 'object' && typeof nextProps.icon === 'object') {
      return JSON.stringify(prevProps.icon) === JSON.stringify(nextProps.icon);
    }
    return false;
  }

  // Compare entity prop by the fields that matter for icon resolution
  if (prevProps.entity !== nextProps.entity) {
    // If one is null/undefined and the other isn't, they're different
    if (!prevProps.entity !== !nextProps.entity) {
      return false;
    }
    
    // If both exist, compare the relevant fields
    if (prevProps.entity && nextProps.entity) {
      return (
        prevProps.entity.entityType === nextProps.entity.entityType &&
        prevProps.entity._id === nextProps.entity._id &&
        prevProps.entity.icon === nextProps.entity.icon
      );
    }
  }

  return true;
});

export default Icon;